/**
 * SyncContext — provides sync state (last synced, online status,
 * in-progress flag) and a triggerSync() action to the whole app.
 */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { runSync } from '../services/syncService';
import { clearProductPhotoCache } from '../services/photoCache';
import { db } from '../services/db';

interface SyncContextType {
  lastSyncedAt: Date | null;
  isOnline: boolean;
  isSyncing: boolean;
  syncError: string | null;
  syncProgress: string;
  /** Bumped on every sync so product-photo URLs bypass the SW/browser cache. */
  photoCacheBust: number;
  triggerSync: () => Promise<void>;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState('');
  const [photoCacheBust, setPhotoCacheBust] = useState(0);
  const syncInFlight = useRef(false);

  // Load last-sync timestamp from IndexedDB on mount
  useEffect(() => {
    db.syncMeta.get('main').then(meta => {
      if (meta?.lastSyncedAt) {
        setLastSyncedAt(new Date(meta.lastSyncedAt));
        setPhotoCacheBust(meta.lastSyncedAt);
      }
    });
  }, []);

  // Online / offline listeners
  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  const triggerSync = useCallback(async () => {
    if (syncInFlight.current || !navigator.onLine) return;
    syncInFlight.current = true;
    setIsSyncing(true);
    setSyncError(null);
    setSyncProgress('Starting sync…');

    try {
      await clearProductPhotoCache();
      const bust = Date.now();
      setPhotoCacheBust(bust);

      const result = await runSync(msg => setSyncProgress(msg), bust);
      setLastSyncedAt(result.syncedAt);
      setSyncProgress(`Synced ${result.itemCount} products`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setSyncError(msg);
      setSyncProgress('');
    } finally {
      setIsSyncing(false);
      syncInFlight.current = false;
      setTimeout(() => setSyncProgress(''), 4000);
    }
  }, []);

  return (
    <SyncContext.Provider
      value={{ lastSyncedAt, isOnline, isSyncing, syncError, syncProgress, photoCacheBust, triggerSync }}
    >
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = (): SyncContextType => {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
};
